import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';
const fmt = (n) => new Intl.NumberFormat('id-ID').format(n || 0);

export default function PemegangSaham({ companyId }) {
  const [list, setList] = useState([]);
  const [docs, setDocs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nama: '', jenis: 'Perorangan', jumlah_saham: '', nominal_per_saham: '', saham_disetor: '', akta_ids: [] });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { loadAll(); }, [companyId]);

  const loadAll = async () => {
    try {
      const params = companyId ? { company_id: companyId } : {};
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/api/shareholders`, { params }),
        axios.get(`${API}/api/documents`, { params })
      ]);
      setList(r1.data);
      setDocs(r2.data.filter(d => d.jenis === 'Akta'));
    } catch {}
  };

  const resetForm = () => setForm({ nama: '', jenis: 'Perorangan', jumlah_saham: '', nominal_per_saham: '', saham_disetor: '', akta_ids: [] });
  const openAdd = () => { setEditing(null); resetForm(); setShowForm(true); };
  const openEdit = (sh) => {
    setEditing(sh.id);
    setForm({ nama: sh.nama, jenis: sh.jenis, jumlah_saham: sh.jumlah_saham, nominal_per_saham: sh.nominal_per_saham, saham_disetor: sh.saham_disetor, akta_ids: sh.akta_ids || [] });
    setShowForm(true);
  };

  const totalSaham = list.reduce((s, x) => s + x.jumlah_saham, 0);
  const totalNilai = list.reduce((s, x) => s + x.total_nilai_saham, 0);
  const totalDisetor = list.reduce((s, x) => s + x.saham_disetor, 0);

  const previewTotal = (parseFloat(form.jumlah_saham) || 0) * (parseFloat(form.nominal_per_saham) || 0);
  const previewBelum = (parseFloat(form.jumlah_saham) || 0) - (parseFloat(form.saham_disetor) || 0);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setMsg('');
    try {
      const params = companyId ? { company_id: companyId } : {};
      const payload = { ...form, jumlah_saham: parseFloat(form.jumlah_saham), nominal_per_saham: parseFloat(form.nominal_per_saham), saham_disetor: parseFloat(form.saham_disetor) };
      if (editing) { await axios.put(`${API}/api/shareholders/${editing}`, payload); }
      else { await axios.post(`${API}/api/shareholders`, payload, { params }); }
      setMsg(editing ? 'Data diperbarui!' : 'Pemegang saham ditambahkan!');
      setShowForm(false); loadAll();
    } catch { setMsg('Gagal menyimpan'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus pemegang saham ini?')) return;
    await axios.delete(`${API}/api/shareholders/${id}`); loadAll();
  };

  const toggleAkta = (id) => setForm(f => ({ ...f, akta_ids: f.akta_ids.includes(id) ? f.akta_ids.filter(x => x !== id) : [...f.akta_ids, id] }));

  if (companyId === null) return <div style={s.empty}>Pilih perusahaan terlebih dahulu dari menu "Semua Perusahaan"</div>;

  return (
    <div>
      <div style={s.header}>
        <h2 style={s.h2}>💰 Pemegang Saham</h2>
        <button style={s.addBtn} onClick={openAdd}>+ Tambah Pemegang Saham</button>
      </div>
      {msg && <div style={{ ...s.msg, color: msg.includes('gagal') ? '#dc2626' : '#16a34a' }}>{msg}</div>}

      {showForm && (
        <div style={s.formCard}>
          <h3 style={s.formTitle}>{editing ? 'Edit Pemegang Saham' : 'Tambah Pemegang Saham'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={s.row}>
              <div style={{ flex: 2 }}>
                <label style={s.label}>Nama Pemegang Saham</label>
                <input style={s.input} value={form.nama} onChange={e => setForm(f => ({ ...f, nama: e.target.value }))} required placeholder="Nama..." />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Jenis</label>
                <select style={s.input} value={form.jenis} onChange={e => setForm(f => ({ ...f, jenis: e.target.value }))}>
                  <option>Perorangan</option>
                  <option>Badan Usaha</option>
                </select>
              </div>
            </div>
            <div style={s.row}>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Jumlah Saham</label>
                <input style={s.input} type="number" value={form.jumlah_saham} onChange={e => setForm(f => ({ ...f, jumlah_saham: e.target.value }))} required placeholder="0" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Nominal per Saham (Rp)</label>
                <input style={s.input} type="number" value={form.nominal_per_saham} onChange={e => setForm(f => ({ ...f, nominal_per_saham: e.target.value }))} required placeholder="0" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Saham Disetor</label>
                <input style={s.input} type="number" value={form.saham_disetor} onChange={e => setForm(f => ({ ...f, saham_disetor: e.target.value }))} required placeholder="0" />
              </div>
            </div>
            {(form.jumlah_saham || form.nominal_per_saham) && (
              <div style={s.preview}>
                <span>Total Nilai: <b>Rp {fmt(previewTotal)}</b></span>
                <span style={{ marginLeft: '16px' }}>Belum Disetor: <b style={{ color: previewBelum > 0 ? '#dc2626' : '#16a34a' }}>{fmt(previewBelum)} saham</b></span>
              </div>
            )}
            {docs.length > 0 && (
              <div style={s.field}>
                <label style={s.label}>Kaitkan dengan Akta</label>
                <div style={s.docList}>
                  {docs.map(d => (
                    <label key={d.id} style={s.checkRow}>
                      <input type="checkbox" checked={form.akta_ids.includes(d.id)} onChange={() => toggleAkta(d.id)} />
                      <span>Akta {d.keterangan_lainnya || ''} v{d.version} - {d.original_filename}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div style={s.formActions}>
              <button type="button" style={s.cancelBtn} onClick={() => setShowForm(false)}>Batal</button>
              <button type="submit" style={s.saveBtn} disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan'}</button>
            </div>
          </form>
        </div>
      )}

      {list.length > 0 && (
        <div style={s.summary}>
          <div style={s.sumItem}><span style={s.sumLabel}>Total Saham</span><span style={s.sumVal}>{fmt(totalSaham)} lembar</span></div>
          <div style={s.sumItem}><span style={s.sumLabel}>Total Nilai Modal</span><span style={s.sumVal}>Rp {fmt(totalNilai)}</span></div>
          <div style={s.sumItem}><span style={s.sumLabel}>Total Disetor</span><span style={s.sumVal}>{fmt(totalDisetor)} lembar</span></div>
        </div>
      )}

      {list.length === 0 ? (
        <div style={s.empty}>Belum ada pemegang saham. Klik "+ Tambah" untuk menambahkan.</div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr style={s.thead}>
                <th style={s.th}>Nama</th>
                <th style={s.th}>Jenis</th>
                <th style={s.th}>Jumlah Saham</th>
                <th style={s.th}>Nominal/Saham</th>
                <th style={s.th}>Total Nilai</th>
                <th style={s.th}>Disetor</th>
                <th style={s.th}>Piutang</th>
                <th style={s.th}>%</th>
                <th style={s.th}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {list.map(sh => (
                <tr key={sh.id} style={s.tr}>
                  <td style={s.td}><b>{sh.nama}</b></td>
                  <td style={s.td}><span style={{ ...s.badge, background: sh.jenis === 'Perorangan' ? '#f0fdf4' : '#eff6ff', color: sh.jenis === 'Perorangan' ? '#16a34a' : '#1d4ed8' }}>{sh.jenis}</span></td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmt(sh.jumlah_saham)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>Rp {fmt(sh.nominal_per_saham)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>Rp {fmt(sh.total_nilai_saham)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmt(sh.saham_disetor)}</td>
                  <td style={{ ...s.td, textAlign: 'right', color: sh.saham_belum_disetor > 0 ? '#dc2626' : '#16a34a' }}>{fmt(sh.saham_belum_disetor)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}><b>{sh.persentase}%</b></td>
                  <td style={s.td}>
                    <button style={s.editBtn} onClick={() => openEdit(sh)}>Edit</button>
                    <button style={s.delBtn} onClick={() => handleDelete(sh.id)}>Hapus</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  h2: { fontSize: '20px', fontWeight: '700', color: '#1a1a2e', margin: 0 },
  addBtn: { padding: '8px 18px', background: '#1a1a2e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  msg: { padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', background: '#f0fdf4' },
  formCard: { background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  formTitle: { fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1a1a2e' },
  row: { display: 'flex', gap: '12px', marginBottom: '12px' },
  field: { marginBottom: '12px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '5px' },
  input: { width: '100%', padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  preview: { background: '#f0fdf4', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px', color: '#374151' },
  docList: { display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '100px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px' },
  checkRow: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' },
  formActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
  cancelBtn: { padding: '8px 18px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer' },
  saveBtn: { padding: '8px 18px', background: '#1a1a2e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' },
  summary: { display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' },
  sumItem: { background: 'white', borderRadius: '10px', padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: '4px', boxShadow: '0 2px 6px rgba(0,0,0,0.05)', minWidth: '180px' },
  sumLabel: { fontSize: '12px', color: '#6b7280' },
  sumVal: { fontSize: '16px', fontWeight: '700', color: '#1a1a2e' },
  tableWrap: { background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '800px' },
  thead: { background: '#f9fafb' },
  th: { padding: '12px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', borderBottom: '1px solid #e5e7eb' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '12px 14px', fontSize: '14px', color: '#374151' },
  badge: { borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: '600' },
  editBtn: { padding: '4px 10px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', marginRight: '6px' },
  delBtn: { padding: '4px 10px', background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
  empty: { background: 'white', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }
};
